// frontend/src/pages/shop/Products.jsx
import { useState, useEffect } from 'react';
import ShopLayout from '../../components/layout/ShopLayout';
import { shopAdminAPI } from '../../services/api';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Form
  const [formData, setFormData] = useState({
    product_name: '',
    category_id: '',
    has_variants: false,
    base_price: '',
    barcode: '',
    sku: '',
    description: '',
    status: 'active'
  });
  
  // Variant Form
  const [variantForm, setVariantForm] = useState({
    variant_name: '',
    price: '',
    sku: '',
    attributes: {}
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (categoryFilter) params.category_id = categoryFilter;
      if (statusFilter) params.status = statusFilter;
      
      const response = await shopAdminAPI.getProducts(params);
      if (response.data.success) {
        setProducts(response.data.data || []);
        setStats(response.data.stats || null);
      }
    } catch (err) {
      console.error('Fetch products error:', err);
      setError(err.response?.data?.message || 'Бараа татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await shopAdminAPI.getCategories();
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (err) {
      console.error('Fetch categories error:', err);
    }
  };

  const handleSearch = () => {
    fetchProducts();
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setFormData({
      product_name: '',
      category_id: '',
      has_variants: false,
      base_price: '',
      barcode: '',
      sku: '',
      description: '',
      status: 'active'
    });
    setShowCreateModal(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      product_name: product.product_name || '',
      category_id: product.category_id || '',
      has_variants: product.has_variants || false,
      base_price: product.base_price || '',
      barcode: product.barcode || '',
      sku: product.sku || '',
      description: product.description || '',
      status: product.status || 'active'
    });
    setShowCreateModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await shopAdminAPI.updateProduct(editingProduct.id, formData);
        alert('Бараа амжилттай шинэчлэгдлээ');
      } else {
        await shopAdminAPI.createProduct(formData);
        alert('Бараа амжилттай үүслээ');
      }
      setShowCreateModal(false);
      fetchProducts();
    } catch (err) {
      alert(err.response?.data?.message || 'Алдаа гарлаа');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Энэ барааг устгахдаа итгэлтэй байна уу?')) return;
    try {
      await shopAdminAPI.deleteProduct(id);
      alert('Бараа амжилттай устлаа');
      fetchProducts();
    } catch (err) {
      alert(err.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const handleViewDetail = async (product) => {
    try {
      const response = await shopAdminAPI.getProductById(product.id);
      if (response.data.success) {
        setSelectedProduct(response.data.data);
        setShowDetailModal(true);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Мэдээлэл татахад алдаа гарлаа');
    }
  };

  const handleAddVariant = (product) => {
    setSelectedProduct(product);
    setVariantForm({
      variant_name: '',
      price: product.base_price || '',
      sku: '',
      attributes: {}
    });
    setShowVariantModal(true);
  };

  const handleVariantSubmit = async (e) => {
    e.preventDefault();
    try {
      await shopAdminAPI.createProductVariant(selectedProduct.id, variantForm);
      alert('Сонголт амжилттай нэмэгдлээ');
      setShowVariantModal(false);
      fetchProducts();
      // Refresh detail modal if open
      if (showDetailModal) {
        handleViewDetail(selectedProduct);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Сонголт нэмэхэд алдаа гарлаа');
    }
  };

  const handleDeleteVariant = async (productId, variantId) => {
    if (!confirm('Энэ сонголтыг устгахдаа итгэлтэй байна уу?')) return;
    try {
      await shopAdminAPI.deleteProductVariant(productId, variantId);
      alert('Сонголт амжилттай устлаа');
      handleViewDetail(selectedProduct);
    } catch (err) {
      alert(err.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('mn-MN').format(amount || 0) + '₮';
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : '-';
  };

  const getTotalStock = (product) => {
    if (product.has_variants && product.variants) {
      return product.variants.reduce((sum, v) => sum + (v.stock_quantity || 0), 0);
    }
    return product.stock_quantity || 0;
  };

  if (loading && products.length === 0) {
    return (
      <ShopLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Уншиж байна...</p>
          </div>
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">📦 Бараа материал</h1>
            <p className="text-gray-500 mt-1">Барааны удирдлага</p>
          </div>
          <button
            onClick={handleCreate}
            className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Бараа нэмэх</span>
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
              <p className="text-blue-100 text-sm">Нийт бараа</p>
              <p className="text-2xl font-bold mt-1">{stats.total_products || 0}</p>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
              <p className="text-green-100 text-sm">Идэвхтэй</p>
              <p className="text-2xl font-bold mt-1">{stats.active_products || 0}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
              <p className="text-purple-100 text-sm">Нийт үлдэгдэл</p>
              <p className="text-2xl font-bold mt-1">{(stats.total_stock || 0).toLocaleString()}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
              <p className="text-orange-100 text-sm">Нийт үнэлгээ</p>
              <p className="text-xl font-bold mt-1">{formatCurrency(stats.total_value)}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Хайх</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Нэр, баркод..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ангилал</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Бүгд</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Төлөв</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Бүгд</option>
                <option value="active">Идэвхтэй</option>
                <option value="inactive">Идэвхгүй</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                🔍 Хайх
              </button>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">📋 Барааны жагсаалт ({products.length})</h3>
          </div>
          
          {products.length === 0 ? (
            <div className="p-12 text-center">
              <span className="text-5xl">📭</span>
              <h3 className="text-xl font-bold text-gray-900 mt-4">Бараа олдсонгүй</h3>
              <p className="text-gray-500 mt-2">Эхний барааг нэмэх үү?</p>
              <button onClick={handleCreate} className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Бараа нэмэх
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Бараа</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Ангилал</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Үнэ</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Үлдэгдэл</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Сонголт</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Төлөв</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Үйлдэл</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            {product.image_url ? (
                              <img src={product.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                            ) : (
                              <span>📦</span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{product.product_name}</p>
                            {product.barcode && <p className="text-xs text-gray-500">{product.barcode}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {getCategoryName(product.category_id)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-semibold text-gray-900">{formatCurrency(product.base_price)}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-semibold ${getTotalStock(product) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {getTotalStock(product)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {product.has_variants ? (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            {product.variants?.length || 0} сонголт
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          product.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {product.status === 'active' ? 'Идэвхтэй' : 'Идэвхгүй'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleViewDetail(product)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Дэлгэрэнгүй"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleEdit(product)}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                            title="Засах"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {product.has_variants && (
                            <button
                              onClick={() => handleAddVariant(product)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                              title="Сонголт нэмэх"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Устгах"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* CREATE/EDIT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingProduct ? '📝 Бараа засах' : '➕ Бараа нэмэх'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Барааны нэр *</label>
                <input
                  type="text"
                  value={formData.product_name}
                  onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ангилал</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Сонгох --</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Үнэ</label>
                  <input
                    type="number"
                    value={formData.base_price}
                    onChange={(e) => setFormData({...formData, base_price: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Баркод</label>
                  <input
                    type="text"
                    value={formData.barcode}
                    onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Тайлбар</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="has_variants"
                  checked={formData.has_variants}
                  onChange={(e) => setFormData({...formData, has_variants: e.target.checked})}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="has_variants" className="text-sm text-gray-700">
                  Сонголттой бараа (өнгө, хэмжээ г.м)
                </label>
              </div>
              
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700">Төлөв:</label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="active"
                    checked={formData.status === 'active'}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  />
                  <span className="text-sm">Идэвхтэй</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="inactive"
                    checked={formData.status === 'inactive'}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  />
                  <span className="text-sm">Идэвхгүй</span>
                </label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Болих
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingProduct ? 'Хадгалах' : 'Үүсгэх'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {showDetailModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">📦 {selectedProduct.product_name}</h2>
              <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                ✕
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Product Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Ангилал</p>
                  <p className="font-medium">{getCategoryName(selectedProduct.category_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Үнэ</p>
                  <p className="font-medium">{formatCurrency(selectedProduct.base_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Баркод</p>
                  <p className="font-medium">{selectedProduct.barcode || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Үлдэгдэл</p>
                  <p className="font-medium">{getTotalStock(selectedProduct)}</p>
                </div>
              </div>
              
              {selectedProduct.description && (
                <div>
                  <p className="text-sm text-gray-500">Тайлбар</p>
                  <p className="text-gray-700">{selectedProduct.description}</p>
                </div>
              )}
              
              {/* Variants */}
              {selectedProduct.has_variants && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">🎨 Сонголтууд</h3>
                    <button
                      onClick={() => handleAddVariant(selectedProduct)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + Сонголт нэмэх
                    </button>
                  </div>
                  
                  {selectedProduct.variants && selectedProduct.variants.length > 0 ? (
                    <div className="space-y-2">
                      {selectedProduct.variants.map((variant) => (
                        <div key={variant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{variant.variant_name}</p>
                            <p className="text-sm text-gray-500">
                              Үнэ: {formatCurrency(variant.price)} | Үлдэгдэл: {variant.stock_quantity || 0}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteVariant(selectedProduct.id, variant.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">Сонголт байхгүй</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* VARIANT MODAL */}
      {showVariantModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">🎨 Сонголт нэмэх</h2>
              <p className="text-sm text-gray-500">{selectedProduct.product_name}</p>
            </div>
            
            <form onSubmit={handleVariantSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Сонголтын нэр *</label>
                <input
                  type="text"
                  value={variantForm.variant_name}
                  onChange={(e) => setVariantForm({...variantForm, variant_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Жишээ: Хар - M"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Үнэ</label>
                <input
                  type="number"
                  value={variantForm.price}
                  onChange={(e) => setVariantForm({...variantForm, price: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  ℹ️ Үлдэгдлийг агуулахын ажилтан орлого хийх үед нэмнэ.
                </p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowVariantModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Болих
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Нэмэх
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </ShopLayout>
  );
};

export default Products;